import useAuth from '@app/auth/use-auth';
import {
  PreviewFeature,
  setPreviewEnabled,
  usePreviewFeature,
} from '@app/utils/preview-features';
import { AccountFeatures } from '@shared/api/types';
import { useSidebar } from '@shared/components/ui/sidebar';
import React, { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AuthenticatedRoutes from '../../authenticated-routes';

export type CustomRouteProps = {
  hasSidebar?: boolean;
  requiresAuth?: boolean;
  element: React.ReactNode;
  feature?: (keyof AccountFeatures)[];
  /** Redirect non-staff users away — for internal/pre-release pages. */
  requiresStaff?: boolean;
  /**
   * Gate behind a preview-feature flag (defaults on outside production).
   * Visiting the route with `?preview=on` / `?preview=off` persists a
   * per-browser override before the gate is applied.
   */
  previewFeature?: PreviewFeature;
  redirectTo?: string;
};

export function CustomRoute({
  hasSidebar = true,
  requiresAuth = true,
  feature,
  requiresStaff = false,
  previewFeature,
  redirectTo = '/',
  element,
}: CustomRouteProps): React.ReactElement {
  const { setVisible } = useSidebar();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const previewEnabled = usePreviewFeature(
    // The hook needs a stable key; an ungated route reads an unused flag.
    previewFeature ?? 'purchasing-insights'
  );

  // Persist a ?preview=on|off override, then drop the param from the URL.
  const previewParam = previewFeature ? searchParams.get('preview') : null;
  useEffect(() => {
    if (!previewFeature || (previewParam !== 'on' && previewParam !== 'off')) {
      return;
    }
    setPreviewEnabled(previewFeature, previewParam === 'on');
    const next = new URLSearchParams(searchParams);
    next.delete('preview');
    setSearchParams(next, { replace: true });
  }, [previewFeature, previewParam, searchParams, setSearchParams]);

  useEffect(() => {
    if (!user || !feature) return;

    const userHasAccessToFeature = feature.some((f) => user.features[f]);
    if (!userHasAccessToFeature) {
      navigate(redirectTo);
    }
  }, [feature, navigate, redirectTo, user]);

  useEffect(() => {
    if (!user || !requiresStaff) return;
    if (!user.isStaff) navigate(redirectTo);
  }, [requiresStaff, navigate, redirectTo, user]);

  useEffect(() => {
    if (!previewFeature) return;
    // Let a pending ?preview=on land before judging the flag.
    if (previewParam === 'on' || previewParam === 'off') return;
    if (!previewEnabled) navigate(redirectTo);
  }, [previewFeature, previewParam, previewEnabled, navigate, redirectTo]);

  useEffect(() => {
    !hasSidebar && setVisible(false);
    return () => setVisible(true);
  }, [hasSidebar, setVisible]);

  if (requiresAuth) {
    return <AuthenticatedRoutes>{element}</AuthenticatedRoutes>;
  }

  return <>{element}</>;
}
